/**
 * Category detail — info + Edit/Delete.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Tag } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { DetailCard, DetailLayout, KeyValue } from "@/components/detail-layout";
import { EntityActions } from "@/components/entity-actions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { categoriesApi, type Category } from "@/lib/api-cadastros";
import { formatDateTime } from "@/lib/format";

export function CategoryDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState<Partial<Category>>({});

  const cat = useQuery({ queryKey: ["category", id], queryFn: () => categoriesApi.get(id), enabled: !!id });

  const update = useMutation({
    mutationFn: (body: Partial<Category>) => categoriesApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["category", id] });
      qc.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Atualizada");
      setEditOpen(false);
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const openEdit = () => {
    if (cat.data) {
      setForm({
        name: cat.data.name,
        slug: cat.data.slug,
        description: cat.data.description,
        active: cat.data.active,
      });
      setEditOpen(true);
    }
  };

  if (!cat.data) return <div className="text-muted-foreground py-10">Carregando…</div>;
  const c = cat.data;

  return (
    <DetailLayout
      backTo="/categories"
      eyebrow="Categoria"
      visual={
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 dark:bg-primary-500/10">
          <Tag className="h-6 w-6" />
        </div>
      }
      title={c.name}
      subtitle={<code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/5 text-xs font-mono">{c.slug}</code>}
      badges={
        c.active ? <Badge variant="success" dot>Ativa</Badge> : <Badge variant="muted" dot>Inativa</Badge>
      }
      actions={
        <EntityActions
          onEdit={openEdit}
          onDelete={() => categoriesApi.remove(id)}
          redirectAfterDelete="/categories"
          invalidateQueries={["categories"]}
          resourceLabel="categoria"
        />
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DetailCard title="Informações">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <KeyValue label="Nome" value={c.name} />
            <KeyValue label="Slug" value={<span className="font-mono">{c.slug}</span>} />
            <KeyValue label="Status" value={c.active ? "Ativa" : "Inativa"} />
            <KeyValue label="Criada em" value={formatDateTime(c.created_at)} />
          </div>
        </DetailCard>
        {c.description && (
          <DetailCard title="Descrição" className="md:col-span-2">
            <p className="text-sm whitespace-pre-wrap">{c.description}</p>
          </DetailCard>
        )}
      </div>

      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Editar categoria</SheetTitle>
          </SheetHeader>
          <SheetBody className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Nome *</Label>
              <Input
                value={form.name ?? ""}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Slug *</Label>
              <Input
                value={form.slug ?? ""}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Descrição</Label>
              <Textarea
                rows={3}
                value={form.description ?? ""}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild><Button variant="outline">Cancelar</Button></SheetClose>
            <Button onClick={() => update.mutate(form)} disabled={update.isPending || !form.name || !form.slug}>
              {update.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </DetailLayout>
  );
}
